import { ICompactProduct } from "api/interfaces/get-page-details/common/CompactProduct.type";
import { IUrlInformation } from "api/interfaces/get-page-details/get-page-details.type";

interface Map {
  [key: string]: string;
}

const sizeOrder = {
  XS: 0, S: 1, M: 2, L: 3, XL: 4, "2XL": 5, "3XL": 6, "4XL": 7,
};

export const sortSizes = (sizes: string[]) => sizes.filter(Boolean).sort((a: string, b: string) => {
  const orderA = sizeOrder[a];
  const orderB = sizeOrder[b];
  return orderA - orderB;
});

export const getApparelVariants = (product: ICompactProduct) => [product, ...(product.alternateProducts || [])] as ICompactProduct[];

export const getApparelSizes = (product: ICompactProduct) => {
  const alternateProducts = product.alternateProducts || [];
  const initialSizes = sortSizes([
    ...alternateProducts
      .filter((apparel) => !apparel.outOfStock)
      .map((apparel) => apparel?.filterableAttributes?.merchandiseSize),
    product.filterableAttributes?.merchandiseSize,
  ]);
  return Array.from(new Set(initialSizes));
};

export const colorMap: Map = {
  White: "/cms/pubicImages/images/White.webp",
  Blue: "/cms/pubicImages/images/blue.webp",
  "Navy Blue": "/cms/pubicImages/images/Navy-Blue.webp",
  "Heather Grey": "/cms/pubicImages/images/Heather-Grey.webp",
  Black: "/cms/pubicImages/images/Black.webp",
  Tangerine: "/cms/pubicImages/images/Tangerine.webp",
  "Deathworld Forest": "/cms/pubicImages/images/Deathwortld-Forest.webp",
  "Heather Stone": "/cms/pubicImages/images/Heather-Stone.webp",
  "Arctic Rain": "/cms/pubicImages/images/Arctic-rain.webp",
  "Vintage Chocolate": "/cms/pubicImages/images/Vintage-Chocolate.webp",
  "White Swan": "/cms/pubicImages/images/White-Swan.webp",
  Multicolor: "/cms/pubicImages/images/Multicolor.webp",
  "Piston Pearl": "/cms/pubicImages/images/Piston%20Pearl.webp",
  "Desert Tan": "/cms/pubicImages/images/Desert%20Tan.webp",
  "Baby Blue": "/cms/pubicImages/images/Baby%20Blue.webp",
  "Washed Black": "/cms/pubicImages/images/Washed%20Black.webp",
  "Midnight Black": "/cms/pubicImages/images/Midnight%20Black.webp",
  "Graphite Dust": "/cms/pubicImages/images/Graphite%20Dust.webp",
  "Summer Green": "/cms/pubicImages/images/Summer%20Green.webp",
};

export const getApparelColors = (product: ICompactProduct) => {
  const colors = getApparelVariants(product)
    .filter((apparel, index) => index === 0 || !apparel.outOfStock)
    .map((apparel) => apparel?.filterableAttributes?.merchandiseColor)
    .filter((color): color is string => Boolean(color && colorMap[color]));
  return Array.from(new Set(colors)).sort();
};

/**
 * Returns the variant of the given color, carrying only the same-color variants as its
 * alternateProducts, so that size / add to cart / quick view stay scoped to that color.
 */
export const getApparelColorVariant = (product: ICompactProduct, color?: string) => {
  const sameColorVariants = getApparelVariants(product).filter((apparel) => apparel?.filterableAttributes?.merchandiseColor === color);
  if (!sameColorVariants.length) {
    return product;
  }
  const selectedSize = product.filterableAttributes?.merchandiseSize;
  const variant = sameColorVariants.find((apparel) => apparel?.filterableAttributes?.merchandiseSize === selectedSize && !apparel.outOfStock)
    || sameColorVariants.find((apparel) => !apparel.outOfStock)
    || sameColorVariants[0];
  return {
    ...variant,
    alternateProducts: sameColorVariants.filter((apparel) => apparel.sku !== variant.sku),
  } as ICompactProduct;
};

export const isApparelProduct = (product: ICompactProduct) => product?.enCategory?.tier0?.toLowerCase() === "merchandise";

export const isMerchandiseCategory = (urlInformation: IUrlInformation | Record<string, never>) => urlInformation?.tier0?.toLowerCase() === "merchandise";
